import type {
  StliteKernelOptions,
  EmscriptenFile,
  EmscriptenFileUrl,
} from "@stlite/react";

export interface ToastOptions {
  disableProgressToasts: boolean;
  disableErrorToasts: boolean;
  disableModuleAutoLoadToasts: boolean;
}

/**
 * Options that control how styles are applied by the browser integration.
 */
export interface StyleOptions {
  /**
   * Controls whether document-level styles (e.g. global CSS rules) are disabled
   * from being mounted into the host document.
   *
   * Set this to `true` in embedded scenarios (such as when mounting inside an
   * existing application or iframe) to avoid unexpected style conflicts with
   * the host page.
   */
  disableDocumentStyles: boolean;
  /**
   * Optional nonce to attach to injected `<style>` elements.
   *
   * This is typically used with a Content Security Policy (CSP) that requires
   * a nonce on inline styles. When provided, the nonce value will be applied
   * to styles created by this library so they are allowed by CSP.
   */
  styleNonce?: string;
}

// Simplified version of StliteKernelOptions for the mount function.
// This is exposed to non-typed consumers of the mount function,
// so all fields are typed as optional.
export type SimplifiedStliteKernelOptions = Partial<{
  entrypoint: string;
  requirements: StliteKernelOptions["requirements"];
  prebuiltPackageNames: StliteKernelOptions["prebuiltPackageNames"];
  files: Record<
    string,
    EmscriptenFile | EmscriptenFileUrl | EmscriptenFile["data"] // EmscriptenFile["data"] is allowed as a shorthand for convenience.
  >;
  archives: StliteKernelOptions["archives"];
  installs: StliteKernelOptions["installs"];
  hostConfig: StliteKernelOptions["hostConfigProperties"];
  pyodideUrl: StliteKernelOptions["pyodideUrl"];
  wheelUrls: StliteKernelOptions["wheelUrls"];
  streamlitConfig: StliteKernelOptions["streamlitConfig"];
  idbfsMountpoints: StliteKernelOptions["idbfsMountpoints"];
  sharedWorker: StliteKernelOptions["sharedWorker"];
  env: StliteKernelOptions["env"];
  languageServer: StliteKernelOptions["languageServer"];
}>;

function canonicalizeFiles(
  files: SimplifiedStliteKernelOptions["files"],
): NonNullable<StliteKernelOptions["files"]> {
  if (files == null) {
    return {};
  }

  const canonicalFiles: StliteKernelOptions["files"] = {};
  Object.keys(files).forEach((key) => {
    const value = files[key];
    if (typeof value === "string" || ArrayBuffer.isView(value)) {
      canonicalFiles[key] = {
        data: value,
      };
      return;
    }
    if (typeof value === "object") {
      if ("data" in value) {
        canonicalFiles[key] = value;
        return;
      } else if ("url" in value) {
        canonicalFiles[key] = {
          ...value,
          url: resolveUrl(value.url),
        };
        return;
      }
    }
    throw new Error(`Invalid file value: ${value}`);
  });
  return canonicalFiles;
}

export function resolveUrl(url: string): string {
  return new URL(url, window.location.href).href;
}

function canonicalizeArchives(
  archives: SimplifiedStliteKernelOptions["archives"],
): StliteKernelOptions["archives"] {
  if (archives == null) {
    return [];
  }

  return archives.map((archive) => {
    if ("buffer" in archive) {
      return archive;
    }

    // Resolve relative URLs before passing to the worker because the base URL is different in the worker.
    return {
      ...archive,
      url: resolveUrl(archive.url),
    };
  });
}

const DEFAULT_ENTRYPOINT = "streamlit_app.py";

export type DetailedMountOptions = SimplifiedStliteKernelOptions &
  Partial<ToastOptions> &
  Partial<StyleOptions>;
export type MountOptions = string | DetailedMountOptions;

export function parseMountOptions(options: MountOptions): {
  kernelOptions: Omit<StliteKernelOptions, "workerType">;
  toastOptions: ToastOptions;
  styleOptions: StyleOptions;
} {
  if (typeof options === "string") {
    const mainScript = options;
    return {
      kernelOptions: {
        entrypoint: DEFAULT_ENTRYPOINT,
        files: {
          [DEFAULT_ENTRYPOINT]: {
            data: mainScript,
          },
        },
        archives: [],
        requirements: [],
        prebuiltPackageNames: [],
      },
      toastOptions: {
        disableProgressToasts: false,
        disableErrorToasts: false,
        disableModuleAutoLoadToasts: false,
      },
      styleOptions: {
        disableDocumentStyles: false,
        styleNonce: undefined,
      },
    };
  }

  const files = canonicalizeFiles(options.files);
  const archives = canonicalizeArchives(options.archives);

  // TODO: Validate the options object using superstruct.
  const entrypoint = options.entrypoint;
  if (entrypoint == null) {
    throw new Error("The `entrypoint` field is required.");
  }

  return {
    kernelOptions: {
      entrypoint,
      files,
      archives,
      requirements: options.requirements || [],
      prebuiltPackageNames: options.prebuiltPackageNames || [],
      hostConfigProperties: options.hostConfig,
      pyodideUrl: options.pyodideUrl,
      streamlitConfig: options.streamlitConfig,
      wheelUrls: options.wheelUrls,
      idbfsMountpoints: options.idbfsMountpoints,
      sharedWorker: options.sharedWorker,
      env: options.env,
      languageServer: options.languageServer,
    },
    toastOptions: {
      disableProgressToasts: options.disableProgressToasts || false,
      disableErrorToasts: options.disableErrorToasts || false,
      disableModuleAutoLoadToasts: options.disableModuleAutoLoadToasts || false,
    },
    styleOptions: {
      disableDocumentStyles: options.disableDocumentStyles ?? false,
      styleNonce: options.styleNonce,
    },
  };
}
